import { useState, useEffect, useCallback } from 'react';

const usePagination = (fetchFunction, initialFilters = {}, initialPageSize = 10) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState(initialFilters);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [additionalData, setAdditionalData] = useState(null);
  // console.log(fetchFunction)

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
        ...filters
      };

      const response = await fetchFunction(params);
      // console.log(response)
      
      if (response.success) {
        // console.log(response)
        setData(response.data.data || response.data);
        setTotalPages(response.data.totalPages || response.data.pages || response.totalPages || 1);
        setTotalItems(response.data.totalItems || response.data.total || response.total || response.data.data?.length || 0);
        setAdditionalData(response.data);
      } else {
        setError(response.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters, sortBy, sortOrder, fetchFunction]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);
  
  const changePageSize = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  const updateSort = (field, order = 'asc') => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  return {
    data,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    additionalData,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters,
    clearFilters,
    updateSort,
    refresh: fetchData
  };
};

export default usePagination;
